/**
 * Header Component
 *
 * Main application header with logo, navigation, and user menu.
 * Reusable across different layouts.
 */

import { useLatestDigest } from '@medsuite/api-client';
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { usePermissions } from '../../hooks/usePermissions';
import { useAuth } from '../../providers/AuthProvider';
import { DigestViewerModal } from '../DigestViewerModal';
import { ThemeToggle } from '../ThemeToggle';

/**
 * Navigation link item type
 */
interface NavLink {
  label: string;
  path: string;
  matchPaths?: string[]; // Additional paths that should highlight this link
  requiresAdmin?: boolean; // If true, only show for Administrator role
}

/**
 * Header component props
 */
interface HeaderProps {
  /**
   * Navigation links to display
   * Default: [Home, Admin]
   */
  navLinks?: NavLink[];

  /**
   * Whether to show the full logo and title or compact version
   * Default: 'full'
   */
  variant?: 'full' | 'compact';

  /**
   * Additional CSS classes to apply to the header
   */
  className?: string;
}

/**
 * Default navigation links
 */
const DEFAULT_NAV_LINKS: NavLink[] = [
  { label: 'Investments', path: '/', matchPaths: ['/investments'] },
  { label: 'Approval Queue', path: '/queue' },
  { label: 'Roadmap', path: '/roadmap', matchPaths: ['/roadmap/*'] },
  { label: 'Admin', path: '/admin', matchPaths: ['/admin/*'], requiresAdmin: true },
  { label: 'About', path: '/about' },
];

/**
 * Header Component
 *
 * Provides the main application header with:
 * - MedSuite logo and title
 * - Navigation links (Investments, Queue, Admin)
 * - User menu placeholder for future authentication
 */
export function Header({
  navLinks = DEFAULT_NAV_LINKS,
  variant: _variant = 'full',
  className = '',
}: HeaderProps) {
  const location = useLocation();
  const { user, logout } = useAuth();
  const { isAdministrator } = usePermissions();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showDigestModal, setShowDigestModal] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Fetch latest digest to determine if the button should be visible
  const { data: latestDigest } = useLatestDigest();

  // Filter nav links based on permissions
  const filteredNavLinks = useMemo(() => {
    return navLinks.filter((link) => {
      if (link.requiresAdmin && !isAdministrator) {
        return false;
      }
      return true;
    });
  }, [navLinks, isAdministrator]);

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Get user display name
  const displayName = user?.name || user?.username || 'User';
  const email = user?.username || '';

  /**
   * Check if a navigation link is active based on current location
   */
  const isLinkActive = (link: NavLink): boolean => {
    // Exact match for root path
    if (link.path === '/' && location.pathname === '/') {
      return true;
    }

    // Check if current path starts with link path (for non-root)
    if (link.path !== '/' && location.pathname.startsWith(link.path)) {
      return true;
    }

    // Check additional match paths
    if (link.matchPaths) {
      return link.matchPaths.some((matchPath) => {
        if (matchPath.endsWith('/*')) {
          const basePath = matchPath.slice(0, -2);
          return location.pathname.startsWith(basePath);
        }
        return location.pathname === matchPath;
      });
    }

    return false;
  };

  /**
   * Get CSS classes for navigation link based on active state
   */
  const getNavLinkClasses = (link: NavLink): string => {
    const isActive = isLinkActive(link);
    const baseClasses = 'rounded-md px-3 py-2 text-sm font-medium transition-colors duration-150';
    const activeClasses = 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-200';
    const inactiveClasses =
      'text-gray-700 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-gray-100';

    return `${baseClasses} ${isActive ? activeClasses : inactiveClasses}`;
  };

  return (
    <header className={`bg-white dark:bg-gray-800 shadow h-[125px] ${className}`}>
      <div className="mx-auto max-w-7xl px-4 h-full sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-full">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center">
              <img
                src="/assets/medsuite-logo-transparent.png"
                alt="MedSuite - Technology Investment Management"
                className="w-[200px] h-auto brightness-0 dark:brightness-0 dark:invert"
                onError={(e) => {
                  // Fallback if logo doesn't load - show text title
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  const fallback = document.createElement('h1');
                  fallback.className =
                    'font-bold tracking-tight text-gray-900 dark:text-gray-100 text-2xl';
                  fallback.textContent = 'MedSuite';
                  target.parentElement?.appendChild(fallback);
                }}
              />
            </Link>
          </div>

          {/* Navigation and User Menu */}
          <div className="flex items-center space-x-4">
            {/* Navigation Links */}
            <nav
              className="flex items-center space-x-2"
              role="navigation"
              aria-label="Main navigation"
            >
              {filteredNavLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={getNavLinkClasses(link)}
                  aria-current={isLinkActive(link) ? 'page' : undefined}
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            {/* Theme Toggle and User Menu */}
            <div className="ml-4 flex items-center gap-2 border-l border-gray-300 dark:border-gray-700 pl-4">
              {/* Daily Digest Button - shown only when a digest exists */}
              {latestDigest && (
                <button
                  type="button"
                  onClick={() => setShowDigestModal(true)}
                  className="flex items-center justify-center rounded-full p-2 text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900 transition-colors"
                  aria-label="View latest daily digest"
                  title="View latest daily digest"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 7.5h1.5m-1.5 3h1.5m-7.5 3h7.5m-7.5 3h7.5m3-9h3.375c.621 0 1.125.504 1.125 1.125V18a2.25 2.25 0 01-2.25 2.25M16.5 7.5V18a2.25 2.25 0 002.25 2.25M16.5 7.5V4.875c0-.621-.504-1.125-1.125-1.125H4.125C3.504 3.75 3 4.254 3 4.875V18a2.25 2.25 0 002.25 2.25h13.5M6 7.5h3v3H6v-3z"
                    />
                  </svg>
                </button>
              )}

              {/* Theme Toggle */}
              <ThemeToggle />

              {/* User Menu */}
              <div className="relative" ref={menuRef}>
                <button
                  type="button"
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center gap-2 rounded-lg bg-gray-100 dark:bg-gray-700 px-3 py-1.5 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900 transition-colors"
                  aria-label="User menu"
                  aria-expanded={showUserMenu}
                  aria-haspopup="true"
                >
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  <span className="text-sm font-medium max-w-[150px] truncate">{displayName}</span>
                  <svg
                    className={`h-4 w-4 transition-transform ${showUserMenu ? 'rotate-180' : ''}`}
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {/* Dropdown Menu */}
                {showUserMenu && (
                  <div className="absolute right-0 mt-2 w-64 rounded-lg bg-white dark:bg-gray-800 shadow-lg ring-1 ring-black ring-opacity-5 dark:ring-gray-700 z-50">
                    <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                        {displayName}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{email}</p>
                    </div>
                    <div className="py-1">
                      <button
                        type="button"
                        onClick={() => {
                          setShowUserMenu(false);
                          logout();
                        }}
                        className="flex w-full items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                      >
                        <svg
                          className="h-4 w-4"
                          fill="none"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                        Sign out
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Daily Digest Modal */}
      <DigestViewerModal
        isOpen={showDigestModal}
        onClose={() => setShowDigestModal(false)}
        digestId={latestDigest?.id ?? null}
      />
    </header>
  );
}

export default Header;
